
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { TeacherSpecializedTutoring } from '@/hooks/useSpecializedTutorings';

interface SpecializedTutoringDialogProps {
  availableTutorings: { name: string }[];
  editingTutoring: TeacherSpecializedTutoring | null;
  onSubmit: (tutoring: string, description?: string) => Promise<boolean>;
  onEditingChange: (tutoring: TeacherSpecializedTutoring | null) => void;
  onAddNewOption: (name: string) => Promise<boolean>;
}

export function SpecializedTutoringDialog({ 
  availableTutorings,
  editingTutoring, 
  onSubmit, 
  onEditingChange,
  onAddNewOption
}: SpecializedTutoringDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    tutoring: '',
    description: '',
    newTutoringName: ''
  });
  const [showNewTutoringInput, setShowNewTutoringInput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let tutoringToUse = formData.tutoring;

    // If adding a new tutoring option
    if (showNewTutoringInput && formData.newTutoringName.trim()) {
      const success = await onAddNewOption(formData.newTutoringName.trim());
      if (!success) return;
      tutoringToUse = formData.newTutoringName.trim();
    }

    if (!tutoringToUse) return;

    const success = await onSubmit(tutoringToUse, formData.description || undefined);

    if (success) {
      resetForm();
      setIsOpen(false);
    }
  };

  const resetForm = () => {
    setFormData({ tutoring: '', description: '', newTutoringName: '' });
    setShowNewTutoringInput(false);
    onEditingChange(null);
  };

  const handleEdit = (tutoring: TeacherSpecializedTutoring) => {
    onEditingChange(tutoring);
    setFormData({
      tutoring: tutoring.specialized_tutorings,
      description: tutoring.description || '',
      newTutoringName: ''
    });
    setShowNewTutoringInput(false);
    setIsOpen(true);
  };

  // Expose handleEdit through useEffect
  React.useEffect(() => {
    if (editingTutoring && !isOpen) {
      handleEdit(editingTutoring);
    }
  }, [editingTutoring]);

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-2" />
          เพิ่มการติวสอบพิเศษ
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingTutoring ? 'แก้ไขการติวสอบพิเศษ' : 'เพิ่มการติวสอบพิเศษ'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="tutoring">รายการติวสอบพิเศษ *</Label>
            {!showNewTutoringInput ? (
              <div className="space-y-2">
                <Select
                  value={formData.tutoring}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, tutoring: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกรายการติวสอบพิเศษ" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTutorings.map((tutoring) => (
                      <SelectItem key={tutoring.name} value={tutoring.name}>
                        {tutoring.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewTutoringInput(true)}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  เพิ่มรายการใหม่
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="ชื่อรายการติวสอบพิเศษใหม่"
                  value={formData.newTutoringName}
                  onChange={(e) => setFormData(prev => ({ ...prev, newTutoringName: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewTutoringInput(false)}
                  className="w-full"
                >
                  เลือกจากรายการที่มี
                </Button>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="description">รายละเอียด</Label>
            <Textarea
              id="description"
              placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                resetForm();
              }}
              className="flex-1"
            >
              ยกเลิก
            </Button>
            <Button type="submit" className="flex-1">
              {editingTutoring ? 'อัพเดท' : 'เพิ่ม'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
